import { Transition } from "@headlessui/react"
import { Form, Head, usePage } from "@inertiajs/react"
import { useState } from "react"

import { FormField } from "@/components/form-field"
import { HeadingSmall } from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

import { DeleteUser } from "./delete-user"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Profile settings",
    href: settingsProfilePath(),
  },
]

function EmailNotifications() {
  const { auth } = usePage().props
  const [checked, setChecked] = useState(auth.user.email_notifications)

  return (
    <div className="space-y-6">
      <HeadingSmall
        title="Email notifications"
        description="One recap email per round, sent to the whole league together"
      />

      <Card>
        <CardContent>
          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
          >
            {({ processing, recentlySuccessful }) => (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="email_notifications"
                    checked={checked}
                    onCheckedChange={(value) => setChecked(value === true)}
                    disabled={processing}
                  />
                  <Input
                    type="hidden"
                    name="email_notifications"
                    value={checked ? "1" : "0"}
                  />
                  <Label
                    htmlFor="email_notifications"
                    className="text-sm font-normal"
                  >
                    Email me the round recap — reply-all to talk trash
                  </Label>
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Save</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-muted-foreground text-sm">Saved</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}

export default function Profile() {
  const { auth } = usePage().props

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Profile information"
            description="Update your name"
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{
              preserveScroll: true,
            }}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <FormField
                  label="Name"
                  htmlFor="name"
                  error={errors.name?.join(", ")}
                >
                  <Input
                    id="name"
                    name="name"
                    className="mt-1 block w-full"
                    defaultValue={auth.user.name}
                    required
                    autoComplete="name"
                    placeholder="Full name"
                  />
                </FormField>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Save</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-muted-foreground text-sm">Saved</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <EmailNotifications />

        <DeleteUser />
      </SettingsLayout>
    </AppLayout>
  )
}
